/**
 * <ec-product-tabs>
 *
 * Tabs (desktop) ↔ accordions (mobile) hybrid for the PDP content area
 * (Description / Détails / Entretien / Livraison & Retours).
 *
 * Markup is the same on both layouts: a row of <button role="tab"> plus
 * one <details> per panel. CSS hides the tab row on mobile and hides the
 * <summary> elements on desktop. This component:
 *   - on desktop, makes tab buttons drive which <details open>
 *   - on mobile, lets each <details> behave natively (toggle on summary
 *     click) and keeps the tab buttons in sync if any
 *   - hydrates ARIA on the dynamic controls (aria-selected, tabindex)
 */

const DESKTOP_QUERY = '(min-width: 768px)';

class EcProductTabs extends HTMLElement {
  #tabs = [];
  #panels = [];
  #mql = null;
  #onMql = null;
  #onClick = null;
  #onKeydown = null;
  #onPanelToggle = null;

  connectedCallback() {
    this.#tabs = Array.from(this.querySelectorAll('[data-ec-tab]'));
    this.#panels = Array.from(this.querySelectorAll('[data-ec-tab-panel]'));

    this.#onClick = (event) => this.#handleClick(event);
    this.addEventListener('click', this.#onClick);

    // WAI-ARIA Authoring Practices: arrow keys move focus across the
    // tablist, with automatic activation. Only on desktop layout — on
    // mobile the tab row is hidden so the handler is a no-op.
    this.#onKeydown = (event) => this.#handleKeydown(event);
    this.addEventListener('keydown', this.#onKeydown);

    // Native <details> toggle (mobile path) — sync ARIA on the buttons.
    this.#onPanelToggle = (event) => this.#handlePanelToggle(event);
    this.#panels.forEach((panel) => {
      panel.addEventListener('toggle', this.#onPanelToggle);
    });

    this.#mql = window.matchMedia(DESKTOP_QUERY);
    this.#onMql = () => this.#applyLayout();
    this.#mql.addEventListener('change', this.#onMql);
    this.#applyLayout();
  }

  disconnectedCallback() {
    if (this.#onClick) this.removeEventListener('click', this.#onClick);
    if (this.#onKeydown) this.removeEventListener('keydown', this.#onKeydown);
    if (this.#onPanelToggle) {
      this.#panels.forEach((panel) => panel.removeEventListener('toggle', this.#onPanelToggle));
    }
    if (this.#mql && this.#onMql) this.#mql.removeEventListener('change', this.#onMql);
  }

  #applyLayout() {
    const isDesktop = this.#mql && this.#mql.matches;

    if (isDesktop) {
      // Open exactly the active panel; close the others.
      const active = this.dataset.activeTab || this.#tabs[0]?.dataset.ecTab;
      this.#panels.forEach((panel) => {
        const isActive = panel.dataset.ecTabPanel === active;
        panel.open = isActive;
      });
      this.#syncTabAria(active);
    } else {
      // Mobile: leave panels in their current open state but make sure
      // the first one is open if everything ended up closed (e.g. after
      // a desktop → mobile swap with no active selection).
      const anyOpen = this.#panels.some((p) => p.open);
      if (!anyOpen && this.#panels[0]) this.#panels[0].open = true;
    }
  }

  #handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-ec-tab]');
    if (tab && this.#mql?.matches) {
      event.preventDefault();
      const key = tab.dataset.ecTab;
      this.#activate(key);
    }
  }

  #handleKeydown(event) {
    if (!this.#mql?.matches) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const tab = target.closest('[data-ec-tab]');
    if (!tab) return;

    const currentIndex = this.#tabs.indexOf(tab);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = this.#tabs[nextIndex];
    if (!(nextTab instanceof HTMLElement)) return;
    this.#activate(nextTab.dataset.ecTab);
    nextTab.focus();
  }

  #handlePanelToggle(event) {
    const panel = event.currentTarget;
    if (!(panel instanceof HTMLDetailsElement)) return;

    if (this.#mql?.matches) {
      // Desktop: enforce single-open. If the user somehow toggled a
      // <details> directly (keyboard), realign with our active tab.
      if (panel.open) {
        const key = panel.dataset.ecTabPanel;
        this.#activate(key, { skipPanel: true });
      }
    } else {
      // Mobile accordion: nothing to enforce, but keep tab ARIA in sync.
      if (panel.open) this.#syncTabAria(panel.dataset.ecTabPanel);
    }
  }

  #activate(key, { skipPanel = false } = {}) {
    if (!key) return;
    this.dataset.activeTab = key;

    if (!skipPanel) {
      this.#panels.forEach((panel) => {
        panel.open = panel.dataset.ecTabPanel === key;
      });
    }
    this.#syncTabAria(key);
  }

  #syncTabAria(key) {
    this.#tabs.forEach((tab) => {
      const isActive = tab.dataset.ecTab === key;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }
}

if (!customElements.get('ec-product-tabs')) {
  customElements.define('ec-product-tabs', EcProductTabs);
}
